import React, { useEffect, useState, useMemo, useRef } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { useDropLocations } from "../../store/useDropLocations";
import { usePlasticDashboard } from "../../store/usePlasticDashboard";
import {
  PlasticVolumesPerWeight,
  PlasticVolumesPerDroppers,
  PlasticVolumesNewDroppers,
  PlasticAvgDropSize,
  PlasticMonthsSinceOpening,
  PlasticActivation,
  KeyMetricsTable,
} from "../../components";
import DropdownFilter from "../../components/modals/DropdownFilter";
import { PrimaryButton } from "../../components";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";

dayjs.extend(isoWeek);

function PlasticVolumesScreen() {
  const { dropLocations, getDropLocations } = useDropLocations();
  const {
    volumesPerWeightData,
    getVolumesPerWeight,
    isLoadingVolumesPerWeight,
    volumesPerDroppersData,
    getVolumesPerDroppers,
    isLoadingVolumesPerDroppers,
    volumesNewDroppersData,
    getVolumesNewDroppers,
    isLoadingVolumesNewDroppers,
    avgDropSizeData,
    getAvgDropSize,
    isLoadingAvgDropSize,
    monthsSinceOpeningData,
    getMonthsSinceOpening,
    isLoadingMonthsSinceOpening,
    monthsSinceOpeningFrom,
    monthsSinceOpeningTo,
    plasticActivationData,
    getPlasticActivation,
    isLoadingPlasticActivation,
    plasticActivationFrom,
    plasticActivationTo,
    keyMetricsData,
    getKeyMetrics,
    isLoadingKeyMetrics,
  } = usePlasticDashboard();

  // Refs for chart screenshots
  const volumesPerWeightRef = useRef(null);
  const volumesPerDroppersRef = useRef(null);
  const volumesNewDroppersRef = useRef(null);
  const avgDropSizeRef = useRef(null);
  const monthsSinceOpeningRef = useRef(null);
  const plasticActivationRef = useRef(null);

  // Screenshot hooks
  const [, takeScreenshot1] = useScreenshot();
  const [, takeScreenshot2] = useScreenshot();
  const [, takeScreenshot3] = useScreenshot();
  const [, takeScreenshot4] = useScreenshot();

  // Download functions
  const downloadVolumesPerWeight = () => {
    takeScreenshot1(volumesPerWeightRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
      link.download = "volumes_per_weight.png";
      link.click();
    });
  };

  const downloadVolumesPerDroppers = () => {
    takeScreenshot2(volumesPerDroppersRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
      link.download = "volumes_per_plastic_droppers.png";
      link.click();
    });
  };

  const downloadVolumesNewDroppers = () => {
    takeScreenshot3(volumesNewDroppersRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
      link.download = "new_plastic_droppers.png";
      link.click();
    });
  };

  const downloadAvgDropSize = () => {
    takeScreenshot4(avgDropSizeRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
      link.download = "average_drop_size.png";
      link.click();
    });
  };

  // Filter type: "locations" or "cities"
  const [filterType, setFilterType] = useState<"locations" | "cities">(
    "cities"
  );

  // Selected cities (when filterType is "cities"); empty = all
  const [selectedCities, setSelectedCities] = useState<string[]>([]);

  // Selected locations (when filterType is "locations")
  // Store the backend location identifiers (e.g. store_name / cost_place), max 5
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  // Period filter
  const [period, setPeriod] = useState<
    "weekly" | "monthly" | "quarterly" | "yearly"
  >("monthly");

  // Year selector (used for quarterly views)
  const [year, setYear] = useState(new Date().getFullYear());
  // Monthly range selection (paired with fromYear/toYear below)
  const [fromMonth, setFromMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.getMonth() + 1;
  });
  const [toMonth, setToMonth] = useState(new Date().getMonth() + 1);

  // For weekly: fromYear + fromWeek + toYear + toWeek
  const [fromYear, setFromYear] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.getFullYear();
  });
  const [fromWeek, setFromWeek] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return dayjs(d).isoWeek();
  });
  const [toYear, setToYear] = useState(new Date().getFullYear());
  const [toWeek, setToWeek] = useState(dayjs().isoWeek());

  useEffect(() => {
    if (period !== "monthly") return;
    if (fromYear > toYear) {
      setToYear(fromYear);
    }
  }, [period, fromYear, toYear]);

  useEffect(() => {
    if (period !== "monthly") return;
    if (fromYear === toYear && fromMonth > toMonth) {
      setToMonth(fromMonth);
    }
  }, [period, fromYear, toYear, fromMonth, toMonth]);

  useEffect(() => {
    if (period !== "monthly") return;
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    if (toYear === currentYear && toMonth > currentMonth) {
      setToMonth(currentMonth);
    }
    if (fromYear === currentYear && fromMonth > currentMonth) {
      setFromMonth(currentMonth);
    }
  }, [period, fromYear, fromMonth, toYear, toMonth]);

  // Fetch locations on mount
  useEffect(() => {
    getDropLocations();
  }, [getDropLocations]);

  // Extract unique cities from locations
  const cities = useMemo(() => {
    if (!dropLocations || dropLocations.length === 0) return [];
    const uniqueCities = Array.from(
      new Set(
        dropLocations
          .map((loc) => loc.city)
          .filter((city): city is string => !!city && city.trim() !== "")
      )
    ).sort();
    return uniqueCities;
  }, [dropLocations]);

  // All available locations (from drop locations), deduplicated by cost_place/name
  const locationOptions = useMemo(() => {
    if (!dropLocations || dropLocations.length === 0) return [];
    const names = Array.from(
      new Set(
        dropLocations
          .map(
            (loc) =>
              (loc.cost_place || loc.name || "").toString().trim()
          )
          .filter((v) => v !== "")
      )
    ).sort();
    return names;
  }, [dropLocations]);

  // Initialize default selected locations (up to 5) when switching to locations filter
  useEffect(() => {
    if (filterType !== "locations") return;
    if (!locationOptions.length) return;
    if (selectedLocations.length === 0) {
      setSelectedLocations(locationOptions.slice(0, 5));
    }
  }, [filterType, locationOptions, selectedLocations.length]);

  // Year options (2024 to current year)
  const yearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );
  const fromYearOptions = yearOptions;
  const toYearOptions = yearOptions;

  const monthOptions = useMemo(
    () => [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ],
    []
  );
  const fromMonthOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const cm = new Date().getMonth() + 1;
    return fromYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [fromYear, monthOptions]);
  const toMonthOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const cm = new Date().getMonth() + 1;
    return toYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [toYear, monthOptions]);

  const getWeekOptions = (y: number) => {
    const last = dayjs(`${y}-12-31`);
    const totalWeeks = last.isoWeek() === 1 ? 52 : last.isoWeek();
    const currentWeek = dayjs().isoWeek();
    const currentYear = dayjs().year();
    return Array.from({ length: totalWeeks }, (_, i) => {
      const w = i + 1;
      const s = dayjs().year(y).isoWeek(w).startOf("isoWeek");
      const e = dayjs().year(y).isoWeek(w).endOf("isoWeek");
      return {
        value: w,
        label: `Week ${w}: ${s.format("MMM DD")} - ${e.format("MMM DD")}`,
        disabled: y === currentYear && w > currentWeek,
      };
    });
  };
  const fromWeekOptions = useMemo(() => getWeekOptions(fromYear), [fromYear]);
  const toWeekOptions = useMemo(() => getWeekOptions(toYear), [toYear]);

  // Prepare API params based on filters
  const apiParams = useMemo(() => {
    const params: any = {
      type: filterType === "locations" ? "location" : "city",
      period: period,
    };

    if (period === "weekly") {
      params.fromYear = fromYear;
      params.fromWeek = fromWeek;
      params.toYear = toYear;
      params.toWeek = toWeek;
    } else if (period === "monthly") {
      params.fromYear = fromYear;
      params.fromMonth = fromMonth;
      params.toYear = toYear;
      params.toMonth = toMonth;
    } else if (period === "quarterly") {
      params.year = year;
    } else if (period === "yearly") {
      params.fromYear = fromYear;
      params.toYear = toYear;
    }

    // Always include arrays so downstream code can safely read `.length`
    if (filterType === "locations") {
      params.locations = selectedLocations ?? [];
    } else {
      params.cities = selectedCities ?? [];
    }

    return params;
  }, [
    filterType,
    period,
    year,
    fromMonth,
    toMonth,
    fromYear,
    fromWeek,
    toYear,
    toWeek,
    selectedLocations,
    selectedCities,
  ]);

  // Prepare API params for bottom charts (Months Since Opening & Plastic Activation)
  // When filterType is "locations", use the same filters as top charts
  // When filterType is "cities", these charts won't be shown
  const bottomChartsApiParams = useMemo(() => {
    // Only create params if filterType is "locations"
    if (filterType !== "locations") {
      return null;
    }

    const params: any = {
      period: period, // Use the same period as top charts
    };

    if (period === "weekly") {
      params.fromYear = fromYear;
      params.fromWeek = fromWeek;
      params.toYear = toYear;
      params.toWeek = toWeek;
    } else if (period === "monthly") {
      params.fromYear = fromYear;
      params.fromMonth = fromMonth;
      params.toYear = toYear;
      params.toMonth = toMonth;
    } else if (period === "quarterly") {
      params.year = year;
    } else if (period === "yearly") {
      params.fromYear = fromYear;
      params.toYear = toYear;
    }

    // Always include array so downstream code can safely read `.length`
    params.locations = selectedLocations ?? [];

    return params;
  }, [
    filterType,
    period,
    year,
    fromMonth,
    toMonth,
    fromYear,
    fromWeek,
    toYear,
    toWeek,
    selectedLocations,
  ]);

  // Fetch data when filters change
  useEffect(() => {
    getVolumesPerWeight(apiParams);
    getVolumesPerDroppers(apiParams);
    getVolumesNewDroppers(apiParams);
    getAvgDropSize(apiParams);
    getKeyMetrics(apiParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParams]);

  // Fetch data for bottom charts (only when filterType is "locations")
  useEffect(() => {
    if (bottomChartsApiParams) {
      getMonthsSinceOpening(bottomChartsApiParams);
      getPlasticActivation(bottomChartsApiParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottomChartsApiParams]);

  return (
    <main>
      {/* Filters */}
      <div className="flex rounded-xl bg-[#C2E5FF] border-[#52B6FF] border-3  flex-col w-full p-2 sm:p-7 border border-gray-border drop-shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div className="flex flex-row items-start justify-start">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
              Filter
            </p>
          </div>
        </div>

        {/* First Row: Filter Type and City/Location */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
          {/* Filter Type: Locations/Cities */}
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text">
              Filter by:
            </label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {filterType === "cities" ? "Cities" : "Locations"}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={filterType === "cities"}
                  onClick={() => {
                    setFilterType("cities");
                    setSelectedCities([]);
                  }}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Cities
                </MenuItem>
                <MenuItem
                  disabled={filterType === "locations"}
                  onClick={() => {
                    setFilterType("locations");
                    setSelectedLocations([]);
                  }}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Locations
                </MenuItem>
              </MenuList>
            </Menu>
          </div>

          {/* Cities Dropdown (when filterType is "cities") */}
          {filterType === "cities" && (
            <div className="flex items-center gap-2">
              <label className="font-satoshi text-sm text-gray-text">
                City:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {selectedCities.length === 0
                      ? "All"
                      : `${selectedCities.length} selected`}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList
                  placeholder={undefined}
                  className="rounded-none max-h-[250px] overflow-y-auto"
                >
                  <MenuItem
                    disabled={selectedCities.length === 0}
                    onClick={() => setSelectedCities([])}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    All
                  </MenuItem>
                  {cities.map((city) => (
                    <MenuItem
                      key={city}
                      onClick={() =>
                        setSelectedCities((prev) =>
                          prev.includes(city)
                            ? prev.filter((c) => c !== city)
                            : [...prev, city]
                        )
                      }
                      className="font-satoshi text-base text-dark flex items-center gap-2"
                      placeholder={undefined}
                    >
                      <span
                        className={`w-4 h-4 rounded border flex items-center justify-center ${
                          selectedCities.includes(city)
                            ? "bg-[#0375FF] border-[#0375FF] text-white"
                            : "border-gray-300"
                        }`}
                      >
                        {selectedCities.includes(city) ? "✓" : ""}
                      </span>
                      {city.charAt(0).toUpperCase() + city.slice(1)}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </div>
          )}

          {/* Locations Dropdown (when filterType is "locations") */}
          {filterType === "locations" && (
            <div className="flex items-center gap-2">
              <label className="font-satoshi text-sm text-gray-text">
                Locations (max 5):
              </label>
              <DropdownFilter
                label={`Locations (${selectedLocations.length}/5)`}
                options={locationOptions}
                selectedOptions={selectedLocations}
                onSelect={(loc: string) => {
                  setSelectedLocations((prev) => {
                    if (prev.includes(loc)) {
                      return prev.filter((l) => l !== loc);
                    }
                    if (prev.length >= 5) {
                      return prev;
                    }
                    return [...prev, loc];
                  });
                }}
                contentClassname="w-full sm:w-auto max-h-[300px] overflow-y-auto"
              />
            </div>
          )}
        </div>

        {/* Second Row: Period and Period Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4 flex-wrap">
          {/* Period Dropdown */}
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text">
              Period:
            </label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "weekly"}
                  onClick={() => setPeriod("weekly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  disabled={period === "monthly"}
                  onClick={() => setPeriod("monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "quarterly"}
                  onClick={() => setPeriod("quarterly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Quarterly
                </MenuItem>
                <MenuItem
                  disabled={period === "yearly"}
                  onClick={() => setPeriod("yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
          </div>

          {/* Monthly: From Year/Month + To Year/Month */}
          {period === "monthly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={fromYear === y}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromMonthOptions.find((m: any) => m.value === fromMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromMonthOptions.map((m: any) => (
                      <MenuItem
                        key={m.value}
                        disabled={fromMonth === m.value}
                        onClick={() => setFromMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={toYear === y}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toMonthOptions.find((m: any) => m.value === toMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toMonthOptions.map((m: any) => (
                      <MenuItem
                        key={m.value}
                        disabled={toMonth === m.value}
                        onClick={() => setToMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}

          {/* Weekly: From Year + From Week + To Year + To Week */}
          {period === "weekly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={fromYear === y}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        fromWeekOptions.find((w: any) => w.value === fromWeek)
                          ?.label
                      }
                    >
                      {fromWeekOptions.find((w: any) => w.value === fromWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {fromWeekOptions.map((w: any) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || fromWeek === w.value}
                        onClick={() => setFromWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={toYear === y}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        toWeekOptions.find((w: any) => w.value === toWeek)
                          ?.label
                      }
                    >
                      {toWeekOptions.find((w: any) => w.value === toWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {toWeekOptions.map((w: any) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || toWeek === w.value}
                        onClick={() => setToWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}

          {/* Quarterly: Only Year */}
          {period === "quarterly" && (
            <div className="flex items-center gap-2">
              <label className="font-satoshi text-sm text-gray-text">
                Year:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {year}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y: number) => (
                    <MenuItem
                      key={y}
                      disabled={year === y}
                      onClick={() => setYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </div>
          )}

          {/* Yearly: From Year + To Year */}
          {period === "yearly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={fromYear === y}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={toYear === y}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Key Metrics Table */}
      <div className="mt-8 mb-8">
        <div className="bg-white rounded-xl border-gray-border drop-shadow-none p-4">
          <KeyMetricsTable
            data={keyMetricsData}
            isLoading={isLoadingKeyMetrics}
            type={filterType === "locations" ? "location" : "city"}
            period={period}
          />
        </div>
      </div>

      {/* Charts */}
      <div className="mt-8 space-y-8">
        {/* Chart 1: Volumes Per Weight */}
        <div
          ref={volumesPerWeightRef}
          className="bg-white rounded-xl border-gray-border drop-shadow-none p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Plastic Weight</h2>
              <Tooltip
                content="Non-cumulative: Shows plastic weight for each selected time period"
                placeholder={undefined}
              >
                <img
                  src={InformationIcon}
                  alt="info"
                  className="inline-block w-4 h-4 cursor-pointer"
                />
              </Tooltip>
            </div>
            <PrimaryButton
              size="sm"
              onClick={downloadVolumesPerWeight}
              label="Download image"
            />
          </div>
          <div>
            <PlasticVolumesPerWeight
              data={volumesPerWeightData}
              isLoading={isLoadingVolumesPerWeight}
              filterType={filterType === "locations" ? "location" : "city"}
              period={period}
            />
          </div>
        </div>

        {/* Chart 2: Volumes Per Plastic Droppers */}
        <div
          ref={volumesPerDroppersRef}
          className="bg-white rounded-xl border-gray-border drop-shadow-none p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Plastic Droppers</h2>
              <Tooltip
                content="Non-cumulative: Shows plastic droppers for each selected time period"
                placeholder={undefined}
              >
                <img
                  src={InformationIcon}
                  alt="info"
                  className="inline-block w-4 h-4 cursor-pointer"
                />
              </Tooltip>
            </div>
            <PrimaryButton
              size="sm"
              onClick={downloadVolumesPerDroppers}
              label="Download image"
            />
          </div>
          <div>
            <PlasticVolumesPerDroppers
              data={volumesPerDroppersData}
              isLoading={isLoadingVolumesPerDroppers}
              filterType={filterType === "locations" ? "location" : "city"}
              period={period}
            />
          </div>
        </div>

        {/* Chart 3: New Plastic Droppers */}
        <div
          ref={volumesNewDroppersRef}
          className="bg-white rounded-xl border-gray-border drop-shadow-none p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">
                New Plastic Droppers Per Month
              </h2>
              <Tooltip
                content="Non-cumulative: Shows new plastic droppers for each selected time period"
                placeholder={undefined}
              >
                <img
                  src={InformationIcon}
                  alt="info"
                  className="inline-block w-4 h-4 cursor-pointer"
                />
              </Tooltip>
            </div>
            <PrimaryButton
              size="sm"
              onClick={downloadVolumesNewDroppers}
              label="Download image"
            />
          </div>
          <div>
            <PlasticVolumesNewDroppers
              data={volumesNewDroppersData}
              isLoading={isLoadingVolumesNewDroppers}
              filterType={filterType === "locations" ? "location" : "city"}
              period={period}
            />
          </div>
        </div>

        {/* Chart 4: Average Drop Size */}
        <div
          ref={avgDropSizeRef}
          className="bg-white rounded-xl border-gray-border drop-shadow-none p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">
                Average Plastic Drop Size
              </h2>
              <Tooltip
                content="Non-cumulative: Shows average plastic drop size for each selected time period"
                placeholder={undefined}
              >
                <img
                  src={InformationIcon}
                  alt="info"
                  className="inline-block w-4 h-4 cursor-pointer"
                />
              </Tooltip>
            </div>
            <PrimaryButton
              size="sm"
              onClick={downloadAvgDropSize}
              label="Download image"
            />
          </div>
          <div>
            <PlasticAvgDropSize
              data={avgDropSizeData}
              isLoading={isLoadingAvgDropSize}
              filterType={filterType === "locations" ? "location" : "city"}
              period={period}
            />
          </div>
        </div>

        {/* Chart 5: Months Since Opening (only shown when filterType is "locations") */}
        {filterType === "locations" && (
          <div
            ref={monthsSinceOpeningRef}
            className="bg-white rounded-xl border-gray-border drop-shadow-none p-4"
          >
            <PlasticMonthsSinceOpening
              data={monthsSinceOpeningData}
              isLoading={isLoadingMonthsSinceOpening}
              period={period}
              from={monthsSinceOpeningFrom}
              to={monthsSinceOpeningTo}
            />
          </div>
        )}

        {/* Chart 6: Plastic Activation (only shown when filterType is "locations") */}
        {filterType === "locations" && (
          <div
            ref={plasticActivationRef}
            className="bg-white rounded-xl border-gray-border drop-shadow-none p-4"
          >
            <PlasticActivation
              data={plasticActivationData}
              isLoading={isLoadingPlasticActivation}
              period={period}
              from={plasticActivationFrom}
              to={plasticActivationTo}
            />
          </div>
        )}
      </div>
    </main>
  );
}

export default withProtectedRoute(PlasticVolumesScreen);
